import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, Form, Input, message, Radio, Typography, Col } from "antd";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { bannerService } from "../../services/banner.service";
import { API_BASE_URL } from "../../config/api.config";
import CardFooterAction from "../../components/CardFooterAction";
import { breadcrumbStore } from "../../store/breadcrumbStore";
import ImageUploader from "../../components/ImageUpload";

export default function BannerForm(){
  const { id } = useParams();
  const [form] = useForm();
  const navigate = useNavigate();
  const { toggle, page } = breadcrumbStore();
  const queryClient = useQueryClient();
  const [imageUrl, setImageUrl] = useState(null);
  const { Text } = Typography;

  const { data: faq } = useQuery({
    queryKey: ['detailFaq', id],
    queryFn: () => bannerService.getById(id),
    enabled: !!id
  });

  const bannerOnly = Form.useWatch('bannerOnly', form);

  useEffect(() => {
    toggle(page.BANNER_CREATE);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if(id && faq?.data){
      form.setFieldsValue(faq?.data);
    }
  }, [faq, form, id]);

  useEffect(() => {
    if (faq?.data?.image) {
      setImageUrl(`${API_BASE_URL}/${faq.data.image}`);
    }
  }, [faq]);

  const storeMutation = useMutation({
    mutationFn: (data) => bannerService.store(data),
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      message.success('Data berhasil disimpan');
      form.resetFields();
      navigate('/banner');
    },
    onError: (error) => {
      console.log(error);
      message.error('Terjadi Kesalahan');
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data) => bannerService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      message.success('Data berhasil diubah');
      form.resetFields();
      navigate('/banner');
    },
    onError: (error) => {
      console.log(error);
      message.error('Terjadi Kesalahan');
    }
  });

  const handleSubmit = (values) => {
    // Get the image file from the form
    const imageFile = form.getFieldValue('image');
    
    if(!id) {
      // For new record, image is required
      if(!imageFile) {
        message.error('File gambar tidak boleh kosong');
        return;
      }
      storeMutation.mutate({ ...values, image: imageFile });
    } else {
      let data = { ...values };
      
      // For update: if there's a new image file, include it
      if(imageFile instanceof File) {
        data.image = imageFile;
      }
      
      updateMutation.mutate(data);
    }
  };

  return (
    <>
      <Card
        title={`${id ? "Ubah" : "Tambah"} Data`}
        actions={[
          <CardFooterAction
            key={1}
            onClick={() => form.submit()}
            route={'/banner'}
          />
        ]}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          <Form.Item hidden name="id">
            <Input/>
          </Form.Item>
          
          <Form.Item
            name="bannerOnly"
            label="Jenis Banner"
          >
            <Radio.Group 
              options={[
                { label: 'Hanya Banner', value: true },
                { label: 'Banner & Deskripsi', value: false },
              ]} 
            />
          </Form.Item>

          <Col xs={24} md={12} style={{ marginTop: 15 }}>
            <ImageUploader
              form={form}
              name="image"
              label="Background"
              imageFromAPI={imageUrl}
            />
            <Text
              type="secondary"
              style={{
                fontSize: "12px",
                display: "block",
                wordBreak: "break-word",
              }}
            >
              Max file size 2MB, tipe file .jpg, .png, & .webp, Ukuran 1920 x 480
            </Text>
          </Col>

          <Form.Item
            name="title"
            label="Judul"
            style={{ marginTop: 20 }}
            rules={[{ required: true, message: 'Judul tidak boleh kosong' }]}
          >
            <Input/>
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Deskripsi"
            rules={[{ required: true, message: 'Deskripsi tidak boleh kosong' }]}
          >
            <Input.TextArea rows={6} />
          </Form.Item>
          
          <Form.Item
            name="textButton"
            label="Button Text"
            rules={[{ required: !bannerOnly, message: 'Button Text tidak boleh kosong' }]}
          >
            <Input disabled={bannerOnly} />
          </Form.Item>
          
          <Form.Item
            name="urlButton"
            label="Button URL"
            rules={[{ required: !bannerOnly, message: 'Button URL tidak boleh kosong' }]}
          >
            <Input disabled={bannerOnly} />
          </Form.Item>
        </Form>
      </Card>
    </>
  );
}